/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IImageProps, Image } from '@fluentui/react/lib/Image';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { ModelType } from 'src/luis/types/ModelType';
import { tutorialMessages } from 'src/luis/verticals/createEntityModal/tutorialMessages';

const messages = defineMessages({
    exampleLabel: {
        id: 'entities.createEntityModal.exampleLabel',
        defaultMessage: 'Example'
    },
    addStructureLabel: {
        id: 'entities.createEntityModal.addStructureLabel ',
        defaultMessage: 'Add structure'
    },
    showSubentitiesLink: {
        id: 'entities.createEntityModal.showSubentitiesLink ',
        defaultMessage: 'Show subentities'
    },
    hideSubentitiesLink: {
        id: 'entities.createEntityModal.hideSubentitiesLink ',
        defaultMessage: 'Hide subentities'
    }
});

const { styleComponent } = getStylistV2('EntityTypeTutorial');

const EntityTypeDescription = styleComponent<LuisTextProps>(LuisText)('EntityTypeDescription', {
    marginBottom: '24px'
});

const ExampleLabelsContainer = styleComponent<IStackProps>(Stack)('ExampleLabelsContainer', {
    marginBottom: '12px'
});

const ExampleStructureToggleLink = styleComponent<ILinkProps>(Link)('ExampleStructureToggleLink', theme => ({
    paddingLeft: '10px',
    fontStyle: 'italic',
    color: theme.exampleStructureToggleLinkColor,
    cursor: 'pointer',
    userSelect: 'none'
}));

const TutorialImage = styleComponent<IImageProps>(Image)('TutorialImage', {
    marginBottom: '8px'
});

const EntityTypeName = styleComponent<LuisTextProps>(LuisText)('EntityTypeName', {
    marginRight: '4px'
});

export const EntityTypeTutorial = (props: { selectedModelType: ModelType }) => {
    const { selectedModelType } = props;
    const content = { label: '', description: '', imageSrc: '', imageDescription: '' };

    const { localize } = useLocalization();
    const [showMLStructureIllustration, setShowMLStructureIllustration] = React.useState(true);

    switch (selectedModelType) {
        case ModelType.Simple:
            content.label = localize(tutorialMessages.machineLearnedEntityLabel);
            content.description = localize(tutorialMessages.machineLearnedEntityDescription);
            content.imageSrc = showMLStructureIllustration
                ? getAssetsUrl('assets/images/entityCreationModal/nodes.svg')
                : getAssetsUrl('assets/images/entityCreationModal/simple.svg');
            content.imageDescription = showMLStructureIllustration
                ? localize(tutorialMessages.machineLearnedEntityImageDescriptionNodes)
                : localize(tutorialMessages.machineLearnedEntityImageDescriptionSimple);
            break;
        case ModelType.List:
            content.label = localize(tutorialMessages.listEntityLabel);
            content.description = localize(tutorialMessages.listEntityDescription);
            content.imageSrc = getAssetsUrl('assets/images/entityCreationModal/list.svg');
            content.imageDescription = localize(tutorialMessages.listEntityImageDescription);
            break;
        case ModelType.Regex:
            content.label = localize(tutorialMessages.regexEntityLabel);
            content.description = localize(tutorialMessages.regexEntityDescription);
            content.imageSrc = getAssetsUrl('assets/images/entityCreationModal/regex.svg');
            content.imageDescription = localize(tutorialMessages.regexEntityImageDescription);
            break;
        case ModelType.PatternAny:
            content.label = localize(tutorialMessages.patternAnyEntityLabel);
            content.description = localize(tutorialMessages.patternAnyEntityDescription);
            content.imageSrc = '';
            content.imageDescription = '';
            break;
        default:
            break;
    }

    return (
        <Stack>
            <EntityTypeDescription lineHeight="1.5">
                <EntityTypeName fontWeight={FontWeights.semibold as Property.FontWeight}>{content.label}</EntityTypeName>
                <LuisText>{content.description}</LuisText>
            </EntityTypeDescription>

            <ExampleLabelsContainer horizontal>
                {selectedModelType !== ModelType.PatternAny && (
                    <LuisText fontWeight="bold" size="12px">
                        {localize(messages.exampleLabel)}
                    </LuisText>
                )}
                {selectedModelType === ModelType.Simple && (
                    <ExampleStructureToggleLink onClick={() => setShowMLStructureIllustration(!showMLStructureIllustration)} size="12px">
                        {showMLStructureIllustration ? localize(messages.hideSubentitiesLink) : localize(messages.showSubentitiesLink)}
                    </ExampleStructureToggleLink>
                )}
            </ExampleLabelsContainer>
            {content.imageSrc && <TutorialImage src={content.imageSrc} alt={content.imageDescription} height={195} shouldFadeIn />}
        </Stack>
    );
};
